<template>
  <div>
    <div class="handover-top-container position-absolute">
      <p class="secondary--text position-relative font-weight-normal">
        Filter by
      </p>
      <div class="d-flex">
        <b-form-group class="filter-by-form-group-1 mr-3">
          <b-form-select
            v-model="selectedPA"
            @change="paChanged"
            :options="paNames"
          ></b-form-select>
        </b-form-group>

        <b-form-group class="mr-3">
          <b-form-select
            v-model="selectedStatus"
            @change="statusChange"
            :options="statuses"
          ></b-form-select>
        </b-form-group>

        <b-form-group
          class="mr-0 filter-by-form-group-2"
          v-if="customerNames.length > 0"
        >
          <b-form-select
            v-model="selectedCustomer"
            :options="customerNames"
            @change="customerChange"
          ></b-form-select>
        </b-form-group>
      </div>
    </div>
    <b-row class="handover-card-row my-5 mx-0">
      <!-- Start card titles -->
      <b-col class="px-3 py-2 handover-card-column d-flex">
        <b-col md="2" class="pl-0 pr-4">
          <p class="primary--text font-family-medium">Customer name</p>
        </b-col>

        <b-col md="2" class="pl-2 pr-4">
          <p class="primary--text font-family-medium">Task</p>
        </b-col>

        <b-col md="1" class="pl-2 pr-4">
          <p class="primary--text font-family-medium">Due date</p>
        </b-col>
        <b-col md="2" class="pl-2 pr-4">
          <p class="primary--text font-family-medium">Assigned from</p>
        </b-col>
        <b-col md="2" class="pl-2 pr-4">
          <p class="primary--text font-family-medium">Created At</p>
        </b-col>
        <b-col md="1" class="pl-2 pr-4">
          <p class="primary--text font-family-medium">Status</p>
        </b-col>
        <b-col md="2" class="pl-2 pr-4">
          <p class="primary--text font-family-medium">Action</p>
        </b-col>
      </b-col>
      <!-- End card titles -->

      <!-- Start handover cards -->
      <b-col
        md="12"
        xl="12"
        lg="12"
        class="px-2 py-2 handover-card-column w-100"
      >
        <b-card
          @click="onTaskCardClick(task)"
          no-body
          v-for="(task, index) in taskList"
          :key="index"
          :class="{
            'mb-2': true,
            'cursor-pointer': task.status !== 'rejected'
          }"
        >
          <b-card-body
            class="px-2 py-2 d-flex align-items-center"
            @mouseenter="showHover(index)"
            @mouseleave="hideHover"
          >
            <b-col md="2" class="pl-0 pr-4 d-flex align-items-center">
              <b-avatar
                v-if="task.task.created_for.avatar_thumbnail !== null"
                size="4.4rem"
                :src="task.task.created_for.avatar_thumbnail"
              ></b-avatar>
              <b-avatar
                v-else
                size="4.4rem"
                class="bg--primary customer-avatar"
                :text="customerInitials(task.task.created_for)"
              ></b-avatar>
              <h6 class="font-sm font-family-medium primary--text ml-3">
                {{ ellipsify(`${task.task.created_for.first_name} ${task.task.created_for.last_name}`, 30) }}
              </h6>
            </b-col>
            <b-col md="2" xl="2" class="pl-2 pr-4 task-column">
              <h6 class="font-sm primary--text font-family-medium">
                {{
                  task.task.title.length > 15
                    ? `${task.task.title.substring(0, 12)}...`
                    : task.task.title
                }}
              </h6>
              <h6
                class="font-sm secondary--text font-weight-normal reduce-whitespace-2"
              >
                {{ trimDescription(task.task.description) }}
              </h6>
            </b-col>
            <b-col md="1" class="pl-2 pr-4">
              <h6 class="font-sm primary--text font-family-regular">
                {{
                  task.task.due_date
                    ? getFormattedDate(task.task.due_date)
                    : "N/A"
                }}
              </h6>
            </b-col>
            <b-col md="2" class="pl-2 pr-4 d-flex align-items-center">
              <b-avatar
                v-if="task.requested_by.avatar_thumbnail"
                size="4.4rem"
                :src="task.requested_by.avatar_thumbnail"
              ></b-avatar>
              <b-avatar
                v-else
                class="bg--primary pa-avatar"
                size="4.4rem"
                :text="customerInitials(task.requested_by)"
              ></b-avatar>
              <h6
                style="width: 70%"
                class="font-sm font-family-medium primary--text ml-3 mt-1"
              >
                {{
                  ellipsify(`${task.requested_by.first_name} ${task.requested_by.last_name}`, 30)
                }}
              </h6>
            </b-col>
            <b-col md="2" class="pl-2 pr-4">
              <h6 class="font-sm primary--text font-family-regular">
                {{ task.created ? getFormattedDate(task.created) : "N/A" }}
              </h6>
            </b-col>
            <b-col md="1" style="width: 10rem" class="pl-2 pr-4 d-flex">
              <img
                v-if="task.task.status === 'Unassigned'"
                class="position-relative"
                src="@/assets/icons/circle_new_task.svg"
                alt=""
              />
              <img
                v-if="task.task.status === 'Today'"
                class="position-relative"
                src="@/assets/icons/circle_today.svg"
                alt=""
              />
              <img
                v-if="task.task.status === 'Later'"
                class="position-relative"
                src="@/assets/icons/circle_later.svg"
                alt=""
              />
              <img
                v-if="task.task.status === 'In Progress'"
                class="position-relative"
                src="@/assets/icons/circle_in_progress.svg"
                alt=""
              />
              <img
                v-if="task.task.status === 'Complete'"
                class="position-relative"
                src="@/assets/icons/circle_complete.svg"
                alt=""
              />
              <img
                v-if="task.task.status === 'Daily'"
                class="position-relative"
                src="@/assets/icons/circle_complete.svg"
                alt=""
              />
              <h6
                class="font-sm primary--text font-family-medium position-relative ml-2 mt-2"
              >
                {{ task.task.status }}
              </h6>
            </b-col>
            <b-col md="2" class="pl-2 pr-4 d-flex actions-column">
              <b-button
                v-if="task.status === 'noaction'"
                class="btn-success btn-pill py-2 mr-3 handover-cta-btns"
                @click.stop="acceptClientTask(task.id)"
                data-cy="accept-handover-btn"
              >
                <h6 class="font-sm font-family-medium d-inline">Accept</h6>
              </b-button>
              <b-button
                v-if="task.status === 'accepted'"
                class="btn-success btn-pill py-2 mr-3 handover-cta-btns"
                disabled
              >
                <h6 class="font-sm font-family-medium d-inline">Accepted</h6>
              </b-button>
              <b-button
                v-if="
                  task.status === 'noaction' ||
                    (task.status === 'accepted' &&
                      task.task.last_tracker.creator.id !== task.assigned_to.id)
                "
                @click.stop="showDecline(task.id)"
                class="bg-none btn-pill py-2 handover-cta-btns"
                data-cy="decline-handover-btn"
              >
                <h6 class="font-sm font-weight-normal d-inline secondary--text">
                  Decline
                </h6>
              </b-button>
              <b-button
                v-if="task.status === 'rejected'"
                disabled
                class="bg-none btn-pill py-2 handover-cta-btns d-flex"
              >
                <h6
                  class="font-sm font-weight-normal d-inline secondary--text mt-2"
                >
                  Declined
                </h6>
              </b-button>
            </b-col>
          </b-card-body>
        </b-card>
      </b-col>
      <div v-if="loading" class=" loading-message">
        <h4 class="primary--text font-family-medium mb-5 mt-5">
          <b-spinner />
        </h4>
      </div>
      <div v-else class=" loading-message">
        <h4 class="primary--text font-family-medium mb-5 mt-5">
          No more tasks to load.
        </h4>
      </div>
      <!-- End handover cards -->
    </b-row>
    <b-modal
      id="delete-checklist-modal"
      v-model="showDeclinePopup"
      body-class="delete-checklist-modal-body"
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <b-row class="pl-4 pr-4 d-flex flex-column">
        <h2 class="font-md modal_header mb-2">
          Please specify a reason *
        </h2>

        <ValidationObserver ref="declineReason" class="mb-5">
          <ValidationProvider
            name="reason"
            rules="required|max:280"
            v-slot="{ errors }"
          >
            <b-form-textarea
              placeholder="Write here..."
              rows="5"
              v-model="reason"
            ></b-form-textarea>
            <h6
              v-if="errors[0]"
              class="font-sm font-family-regular position-absolute danger mt-1"
            >
              {{ errors[0] }}
            </h6>
          </ValidationProvider>
        </ValidationObserver>
      </b-row>
      <div>
        <div class="buttons-row">
          <div class="buttons-wrapper">
            <div class="button-wrapper">
              <b-button
                class="cancel-button"
                @click="showDeclinePopup = false"
                :disabled="isSavingDecline"
                >Cancel</b-button
              >
            </div>
            <div class="button-wrapper">
              <b-button
                class="delete-button"
                @click="declineClientTask"
                :disabled="isSavingDecline"
                >Decline</b-button
              >
            </div>
          </div>
        </div>
      </div>
    </b-modal>
    <b-modal
      id="delete-checklist-modal"
      v-model="showAuthError"
      body-class="delete-checklist-modal-body"
      hide-header
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      centered
    >
      <h1 class="font-lg">Unable to perform action</h1>

      <h3 class="font-lg">
        {{ authMessage }}
      </h3>
      <div class="buttons-row">
        <div class="buttons-wrapper">
          <div class="button-wrapper">
            <b-button class="cancel-button" @click="reloadPage"
              >Cancel</b-button
            >
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import {
  getAssistantsList,
  updateHandOverTask
} from "@/apis/assistants";
import _ from 'lodash'
import { getTaskStatus } from "@/apis/common";
import { ellipsify } from "@/utils/utils";
import {
  ValidationObserver,
  ValidationProvider,
  extend
} from "vee-validate/dist/vee-validate.full";
import moment from "moment";
import { collection, query, onSnapshot, where } from "firebase/firestore";
import { db, firebaseConfig } from "@/config/firestore";

extend("required", {
  message: () => "This is a required field"
});
extend("max", {
  message: () => "This field can contain only 280 characters"
});
export default {
  name: "HandoverToMe",
  data() {
    return {
      infiniteId: +new Date(),
      offset: 0,
      loading: false,
      reason: "",
      authMessage: "",
      taskToDecline: "",
      isSavingDecline: false,
      declineBtnText: "Decline",
      taskList: [],
      count: null,
      paNames: [],
      customerNames: [],
      customersPopulated: false,
      statuses: [],
      selectedPA: null,
      selectedCustomer: null,
      selectedStatus: null,
      activeHoverID: "",
      showAuthError: false,
      expandHover: false,
      isLoading: true,
      showDeclinePopup: false
    };
  },
  components: {
    ValidationObserver,
    ValidationProvider
  },
  created() {
    this.paNames.unshift({
      value: null,
      text: "Assigned from"
    });
    this.statuses.unshift({
      value: null,
      text: "Task Status"
    });
    this.customerNames.unshift({
      value: null,
      text: "Customer"
    });
    this.trackView();
  },
  mounted() {
    getTaskStatus().then(res => {
      this.statuses = [...res.data.results].map(item => {
        item.text = item.title;
        item.value = item.id;
        return item;
      });
      this.statuses.unshift({
        value: null,
        text: "Task Status"
      });
    });
    getAssistantsList().then(res => {
      this.paNames = [...res.data.results]
        .filter(el => el.id !== this.profileUserId)
        .map(item => {
          item.text = `${item.user.firstName} ${item.user.lastName}`;
          item.value = item.id;
          return item;
        });
      this.paNames.unshift({
        value: null,
        text: "Assigned from"
      });
    });
    this.loadTasks()
  },
  beforeDestroy() {
    this.unsubscribe()
  },
  methods: {
    trackView() {
      this.$gtag.event("Handover to me - View", {
        event_category: "feature"
      });
    },
    changeData() {
      this.loadTasks()
    },
    trimDescription(description) {
      let des = description.replace(/<(.|\n)*?>/g, " ").trim();
      if (des.length > 15) {
        return `${des.substring(0, 12)}...`;
      } else if (des.length > 0) {
        return des;
      } else {
        return "...";
      }
    },
    loadTasks() {
      this.loading = true  
      if (this.unsubscribe) {
        this.unsubscribe()
      }
      const params = [
        where('assigned_to.id', '==', this.profileUserId),
        where('status', '==', 'noaction')
      ]
      if (this.selectedStatus) {
        let status = (this.statuses.find(status => status.id === this.selectedStatus)).title
        params.push(where('task.status', '==', status))
      }
      if (this.selectedCustomer) {
        params.push(where('task.created_for.id', '==', this.selectedCustomer))
      }
      if (this.selectedPA) {
        params.push(where('requested_by.id', '==', this.selectedPA))
      }
      const handoversRef = collection(db, firebaseConfig.collectionName)
      const q = query(handoversRef, ...params);
      this.unsubscribe = onSnapshot(q, (querySnapshot) => {
        const taskList = [];
        querySnapshot.forEach((doc) => {
            taskList.push({
              ...doc.data(),
              id: doc.id
            });
        });
        this.taskList = taskList
        this.loading = false

        if (!this.customersPopulated) {
          this.populateCustomers()
          this.customersPopulated = true
        }
      });
    },
    populateCustomers() {
      let customerNames = []
      for (let task of this.taskList) {
        let customer = task.task.created_for
        customerNames.push({
          value: customer.id,
          text: `${customer.first_name} ${customer.last_name}`
        })
      }
      customerNames.sort((a, b) => a.text < b.text ? -1: 1)
      customerNames.unshift({
        value: null,
        text: 'Customer'
      })
      this.customerNames = _.uniqBy(customerNames, 'value')
    },
    onTaskCardClick(task) {
      if (task.status !== "rejected") {
        this.$router.push({ path: `/task/${task.task.id}` });
      }
    },
    reloadPage() {
      this.showAuthError = false;
      this.$router.go();
    },
    getFormattedDate(date) {
      return moment(date).format("MM/DD/YYYY");
    },
    closeModal() {
      this.showDeclinePopup = false;
    },
    showHover(index) {
      this.expandHover = true;
      this.activeHoverID = this.taskList[index].id;
    },
    hideHover() {
      this.expandHover = false;
      this.activeHoverID = "";
    },
    infiniteHandlerHandoverTasks($state) {
      $state.complete()
    },
    statusChange() {
      this.changeData();
    },
    paChanged() {
      this.changeData();
    },
    customerChange() {
      this.changeData();
    },
    acceptClientTask(id) {
      const data = {
        status: "accepted"
      };
      updateHandOverTask(id, data)
        .then(() => {
          console.log("success");
        })
        .catch(err => {
          console.log(err);
          this.showAuthError = true;
          this.authMessage = err.response.data.detail;
        });
    },
    showDecline(id) {
      this.showDeclinePopup = true;
      this.taskToDecline = id;
    },
    async declineClientTask() {
      const isValid = await this.$refs.declineReason.validate();
      if (isValid) {
        this.isSavingDecline = true;
        const data = {
          status: "rejected",
          reason: this.reason
        };
        updateHandOverTask(this.taskToDecline, data)
          .then(() => {
            this.isSavingDecline = false;
            this.showDeclinePopup = false;
            this.reason = "";
          })
          .catch(err => {
            console.log(err.response);
            this.showDeclinePopup = false;
            this.isSavingDecline = false;
            this.showAuthError = true;
            this.authMessage = err.response.data.detail;
          });
      }
    },
    ellipsify,
    customerInitials(user) {
      let fullName = `${user.first_name} ${user.last_name}`;
      return fullName.match(/\b(\w)/g).join("");
    }
  },
  computed: {
    ...mapState("authModule", ["role", "profileUserId"])
  }
};
</script>

<style scoped lang="scss">
.handover-top-container {
  top: -4rem;
  right: 0;
  z-index: 2;
}
button.btn.close-btn {
  right: -2.5rem;
  top: -2rem;
  background: none;
}
.task-column {
  h6 {
    margin: 0;
  }
}
.handover-top-container {
  top: -9.7rem;
}
.actions-column {
  display: flex;
  justify-content: space-between;
  padding-right: 0;
  padding-left: 20px;
}
.loading-message {
  text-align: center;
  width: 100%;
}
</style>
